import { Injectable } from '@angular/core';
import { Empresa } from '../models/empresa';
import { BehaviorSubject, Observable, map, of } from 'rxjs';
import { Escala } from '../models/escala';
import { Local } from '../models/local';
import { Colaborador } from '../models/colaborador';
import { StorageKeys } from '../models/storage_keys';

interface HasId {
  id?: number;
}

type EscalaLocal = {
  local: Local;
  escalas: Escala[];
} & HasId;

@Injectable({
  providedIn: 'root',
})
export class ScaleStore {
  private empresas = new BehaviorSubject<Empresa[]>([]);
  empresas$: Observable<Empresa[]> = this.empresas.asObservable();

  private escalas = new BehaviorSubject<EscalaLocal[]>([]);
  escalas$: Observable<EscalaLocal[]> = this.escalas.asObservable();

  private locais = new BehaviorSubject<Local[]>([]);
  locais$: Observable<Local[]> = this.locais.asObservable();

  private colaboradores = new BehaviorSubject<Colaborador[]>([]);
  colaboradores$: Observable<Colaborador[]> = this.colaboradores.asObservable();

  private empresaSelected = new BehaviorSubject<Empresa>({} as Empresa);
  empresaSelected$: Observable<Empresa> = this.empresaSelected.asObservable();

  constructor() {}

  setLocais(locaisData: Local[]) {
    this.setData(locaisData, this.locais, StorageKeys.LOCALES_DATA);
  }

  setEmpresas(empresasData: Empresa[]) {
    this.setData(empresasData, this.empresas, StorageKeys.EMPRESAS_DATA);
  }

  setEmpresaSelectedStorage(empresasData: Empresa) {
    localStorage.setItem(
      StorageKeys.EMPRESA_DATA,
      JSON.stringify(empresasData)
    );
  }

  getEmpresaSelectedStorage(): Empresa | null {
    return JSON.parse(
      localStorage.getItem(StorageKeys.EMPRESA_DATA)!
    ) as Empresa | null;
  }

  setEscalas(local: Local, escalasData: Escala[]) {
    const data = this.escalas.value;
    data.push({
      local: local,
      escalas: escalasData,
      id: local.id,
    });
    console.log(data, 'setEscalas');
    this.escalas.next(data);
  }

  setColaboradores(colaboradoresData: Colaborador[]) {
    this.setData(
      colaboradoresData,
      this.colaboradores,
      StorageKeys.COLABORADORES_DATA
    );
  }

  setEmpresaSelected(empresaData: Empresa) {
    this.escalas.next([]);
    this.empresaSelected.next({} as any);
    this.setData(empresaData, this.empresaSelected);
    this.setEmpresaSelectedStorage(empresaData);
  }

  getEmpresaSelected(): Empresa {
    return this.getData(this.empresaSelected);
  }

  getEmpresas(): Empresa[] {
    return this.getData(this.empresas);
  }

  getLocais(): Local[] {
    return this.getData(this.locais);
  }

  getLocaisNotEmpty() {
    return this.escalas$.pipe(
      map((escalas) => {
        const locais = escalas.filter(
          (escala) => escala.escalas.length != 0 && escala.local.ativo == true
        );
        if (locais) {
          return locais.map((escala) => escala.local);
        }
        return [];
      })
    );
  }

  getEscalas(LocalId: number): Escala[] {
    const dataFiltered = this.escalas.value.find(
      (escala) => escala.local.id === LocalId
    );
    const data = dataFiltered?.escalas!;
    return data;
  }

  getEscalaO(localId: number): Observable<Escala[]> {
    return this.escalas$.pipe(
      map((res) => {
        if (res) {
          return res.find((escala) => escala.local.id === localId)!.escalas;
        }
        return [];
      })
    );
  }

  getLocaisAtivos() {
    return this.locais$.pipe(
      map((res) => {
        return res.filter((local) => local.ativo === true);
      })
    );
  }

  addEventInCollaborator(collaboratorId: any, data: any) {
    const colaborador = this.colaboradores.value.find(
      (collaborator) => collaborator.id === collaboratorId
    );
    if (colaborador) {
      colaborador.afastamentos.push(data);
      this.updateData(collaboratorId!, colaborador, this.colaboradores);
    }
  }

  removeEventInCollaborator(collaboratorId: any, eventId: number) {
    const colaborador = this.colaboradores.value.find(
      (collaborator) => collaborator.id === collaboratorId
    );
    if (colaborador) {
      colaborador.afastamentos = colaborador.afastamentos.filter(
        (a) => a.id != eventId
      );
      this.updateData(collaboratorId!, colaborador, this.colaboradores);
    }
  }

  getColaboradores(): Colaborador[] {
    return this.getData(this.colaboradores);
  }

  addColaborador(colaborador: Colaborador): void {
    this.addData(colaborador, this.colaboradores);
  }

  updateColaborador(colaborador: Colaborador): void {
    this.updateData(colaborador.id!, colaborador, this.colaboradores);
  }

  deleteEscala(localId: number, escalaId: number): void {
    const currentData = this.escalas.value;
    const index = currentData.findIndex((item) => item.id === localId);

    if (index == -1) return;

    const escalas = currentData[index].escalas.filter(
      (escala) => escala.id != escalaId
    );

    currentData[index].escalas = escalas;
    this.escalas.next(currentData);
  }

  addEscala(newEscala: Escala): void {
    const localId = newEscala.local!.id;
    const index = this.escalas.value.findIndex(
      (data: EscalaLocal) => data.local.id === localId
    );
    const newData = this.escalas.value;
    if (index == -1) {
      newData.push({
        local: newEscala.local!,
        escalas: [newEscala],
        id: localId,
      });
      this.escalas.next(newData);
      return;
    }
    newData[index].escalas.push(newEscala);
    console.log(newData, index);
    this.escalas.next(newData);
  }

  updateEscala(newData: any): void {
    const localId = newData.localId;
    const indexEscala = this.escalas.value.findIndex(
      (sl) => sl.local.id == localId
    );
    if (indexEscala != -1) {
      const arrayData = this.escalas.value;
      const escalas = arrayData[indexEscala].escalas;
      const escalaFiltered = escalas.filter((x) => x.id != newData.id);
      const escala = this.escalas.value[indexEscala].escalas.find(
        (x) => x.id == newData.id
      );
      const newChanges = {
        ...escala,
        ...newData,
      };

      escalaFiltered.push(newChanges);
      arrayData[indexEscala].escalas = escalaFiltered;
      this.escalas.next(arrayData);
    }
  }

  addEmpresa(empresa: Empresa): void {
    this.addData(empresa, this.empresas);
  }

  addEmpresas(empresas: Empresa[]): void {
    const datas = this.empresas.value;
    datas.concat(empresas);
    this.setData(datas, this.empresas, StorageKeys.EMPRESAS_DATA);
    // this.empresas.next(newDatas);
  }

  updateEmpresa(empresa: Empresa): void {
    this.updateData(empresa.id!, empresa, this.empresas);
    if (this.empresaSelected.value.id === empresa.id) {
      this.empresaSelected.next(empresa);
      console.log('set');
    }
  }

  addLocal(local: Local): void {
    this.addData(local, this.locais);
    const escalaData = this.escalas.value;
    escalaData.push({
      local: local,
      escalas: [],
    });
    this.escalas.next(escalaData);
  }

  updateLocal(local: Local): void {
    this.updateData(local.id!, local, this.locais);

    console.log('updateLocal ', local);
    const localId = local.id;
    console.log('escalas value = ', this.escalas.value);
    const localPossuiEscala = this.escalas.value.some(
      (d: any) => d.local.id === localId
    ); //
    console.log('localPossuiEscala = ', localPossuiEscala);

    if (!localPossuiEscala) return; // se nao houver escala para o local

    const escalasDeOutrosLocais = this.escalas.value.filter(
      (d2: any) => d2.local.id !== localId
    );
    console.log(
      'escalasDeOutrosLocais = ',
      JSON.stringify(escalasDeOutrosLocais)
    );

    const escalasDoLocal = this.escalas.value.filter(
      (d2: any) => d2.local.id == localId
    );
    console.log('escalasDoLocal = ', JSON.stringify(escalasDoLocal));

    escalasDoLocal.forEach((escala) => {
      escala.local = local;
      escala.escalas.forEach((escalaInterna) => {
        escalaInterna.local = local;
      });
    });
    console.log('escalasDoLocal = ', JSON.stringify(escalasDoLocal));

    const novasEscalas = escalasDeOutrosLocais.concat(escalasDoLocal);
    console.log('todas escalas ', JSON.stringify(novasEscalas));
    this.escalas.next(novasEscalas);
  }

  removeLocal(localId: number): void {
    this.removeData(localId, this.locais);
    const index = this.escalas.value.findIndex(
      (data: any) => data.local.id === localId
    );
    const newData = this.escalas.value;
    newData.splice(index, 1);
    this.escalas.next(newData);
  }

  removeEmpresa(empresaId: number): void {
    this.removeData(empresaId, this.empresas);
  }

  clearData() {
    localStorage.removeItem(StorageKeys.LOCALES_DATA);
    localStorage.removeItem(StorageKeys.EMPRESAS_DATA);
    localStorage.removeItem(StorageKeys.ESCALAS_DATA);
    localStorage.removeItem(StorageKeys.COLABORADORES_DATA);
    // localStorage.removeItem(StorageKeys.USERDATA);
    // localStorage.removeItem(StorageKeys.TOKEN);
    localStorage.removeItem(StorageKeys.USERADMIN);
  }

  private setData<T>(data: T, subject: BehaviorSubject<T>, key?: StorageKeys) {
    subject.next(data);
    if (key) {
      localStorage.setItem(key, JSON.stringify(data));
    }
  }

  private getData<T>(subject: BehaviorSubject<T>): T {
    return subject.value;
  }

  private addData<T extends HasId>(data: T, subject: BehaviorSubject<T[]>) {
    const currentData = subject.value;
    currentData.push(data);
    subject.next(currentData);
  }

  private addDatas<T extends HasId>(datas: T[], subject: BehaviorSubject<T[]>) {
    const currentData = subject.value;
    currentData.push(...datas);
    subject.next(currentData);
  }

  private updateData<T extends HasId>(
    id: number,
    data: T,
    subject: BehaviorSubject<T[]>
  ) {
    const currentData = subject.value;
    const index = currentData.findIndex((item) => item.id === id);
    if (index > -1) {
      currentData[index] = data;
      subject.next(currentData);
    }
  }

  private removeData<T extends HasId>(
    id: number,
    subject: BehaviorSubject<T[]>
  ) {
    const currentData = subject.value;
    const index = currentData.findIndex((item) => item.id === id);
    if (index > -1) {
      currentData.splice(index, 1);
      subject.next(currentData);
    }
  }

  removeColaborador(colaboradorId: number): void {
    this.removeData(colaboradorId, this.colaboradores);
  }
}
